/**
 * What this session may do — fetched once, read everywhere.
 *
 * A context rather than a hook per component, because a dozen screens ask the
 * same question and `GET /api/auth/capabilities` should be answered once per
 * session rather than once per mount. It sits inside AuthProvider, so it can
 * refetch when the session changes and clear when it ends.
 *
 * **This is a rendering aid, not a security boundary.** Every capability it
 * reports is re-checked server-side on the request that uses it; hiding a button
 * is a courtesy to the user, not a control. The house rule is stated at the top
 * of lib/aiAccess.js and it applies here unchanged.
 *
 * Which is why this fails CLOSED. `useAiAnalysis` fails open — it returns
 * `canUseAi: true` when the capabilities call errors — and the result is that
 * during an outage every account sees the AI button and only the server's
 * refusal stops them. That is defensible for one button whose backend refuses
 * anyway. It is not defensible as a general rule: a permission list that
 * defaults to "everything" while loading would flash the entire hospital admin
 * menu at a receptionist on every slow connection. So `can()` is false until an
 * answer arrives, and screens render their loading state rather than their
 * denied state while `loading` is true.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { authApi } from '../api/endpoints';
import { useAuth } from './AuthContext';

const PermissionContext = createContext(null);

const EMPTY = Object.freeze([]);

export function PermissionProvider({ children }) {
  const { isAuthenticated, session } = useAuth();

  const [state, setState] = useState({
    permissions: EMPTY,
    institutionId: null,
    memberRole: null,
    servesDoctorId: null,
    loading: false,
    error: null,
  });

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const data = await authApi.capabilities();
      setState({
        permissions: Array.isArray(data?.permissions) ? data.permissions : EMPTY,
        institutionId: data?.institutionId ?? null,
        memberRole: data?.memberRole ?? null,
        servesDoctorId: data?.servesDoctorId ?? null,
        loading: false,
        error: null,
      });
    } catch (err) {
      // Fail closed: no capabilities until the server says otherwise. The
      // screens distinguish this from a denial by checking `loading`/`error`.
      console.error('[Permissions] Could not load capabilities:', err.message);
      setState({
        permissions: EMPTY,
        institutionId: null,
        memberRole: null,
        servesDoctorId: null,
        loading: false,
        error: err.message,
      });
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setState({
        permissions: EMPTY,
        institutionId: null,
        memberRole: null,
        servesDoctorId: null,
        loading: false,
        error: null,
      });
      return;
    }
    load();
  }, [isAuthenticated, session?.id, load]);

  const value = useMemo(() => {
    const held = state.permissions;
    return {
      ...state,
      /** Does this session hold every one of the named capabilities? */
      can: (...required) => required.flat().every((name) => held.includes(name)),
      /** At least one of them? */
      canAny: (...required) => required.flat().some((name) => held.includes(name)),
      /** Re-read after something changed a privilege. */
      refresh: load,
    };
  }, [state, load]);

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
}

export function usePermissions() {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used inside a PermissionProvider');
  }
  return context;
}

export default PermissionContext;
